import { useState } from "react";
import { Box, Typography, Button, Stack, Chip, Tooltip } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Flag as FlagIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon,
  ArrowForward as ArrowForwardIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import type {
  ReminderWithUser,
  ShipmentOverviewInfo,
} from "../../../src/shipments/reminders/types";
import { apiClient } from "../utils/apiClient";
import { formatDateMonthDay } from "../utils/time";

interface ReminderWithOptionalShipmentOverview extends ReminderWithUser {
  shipmentOverview?: ShipmentOverviewInfo | null;
  isInternal?: boolean;
}

interface ShipmentReminderComponentProps {
  reminder: ReminderWithOptionalShipmentOverview;
  showShipmentOverviewInfo?: boolean;
  onShipmentClick?: (shipmentId: string) => void;
  onReminderUpdated?: (reminderId: string, resolved: boolean) => void;
  onEmailOpen?: (emailId: string) => void;
}

const getReminderCategory = (type: string): "exception" | "milestone" => {
  const milestoneTypes: string[] = [];
  return milestoneTypes.includes(type) ? "milestone" : "exception";
};

interface ShipmentOverviewTileProps {
  shipmentOverview: ShipmentOverviewInfo;
  onOpenShipment?: () => void;
}

const ShipmentOverviewTile: React.FC<ShipmentOverviewTileProps> = ({
  shipmentOverview,
  onOpenShipment,
}) => {
  const { pol, pod, bookingNumber, masterBillOfLading, containerNumbers } =
    shipmentOverview;

  // Format container numbers display
  const containerDisplay =
    containerNumbers.length > 0
      ? containerNumbers.length === 1
        ? containerNumbers[0]
        : `${containerNumbers[0]} (+${containerNumbers.length - 1})`
      : null;

  return (
    <Stack spacing={0.25}>
      {/* Open Shipment Link */}
      {onOpenShipment && (
        <Box
          component="button"
          onClick={onOpenShipment}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "#1976d2",
            "&:hover": {
              color: "#1565c0",
            },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            Open Shipment
          </Typography>
        </Box>
      )}

      {/* Line 1: POL → POD */}
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="body2"
          sx={{ fontSize: "0.875rem", color: "text.secondary" }}
        >
          {pol || "—"}
        </Typography>
        <ArrowForwardIcon
          sx={{ fontSize: 12, color: "text.secondary" }}
        />
        <Typography
          variant="body2"
          sx={{ fontSize: "0.875rem", color: "text.secondary" }}
        >
          {pod || "—"}
        </Typography>
      </Stack>

      {/* Line 2: Booking Number */}
      {bookingNumber && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
        >
          Booking #: {bookingNumber}
        </Typography>
      )}

      {/* Line 3: Master Bill of Lading */}
      {masterBillOfLading && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
        >
          BL: {masterBillOfLading}
        </Typography>
      )}

      {/* Line 4: Container Numbers with Tooltip */}
      {containerDisplay &&
        (containerNumbers.length > 1 ? (
          <Tooltip title={containerNumbers.join(", ")} arrow>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
                textDecoration: "underline",
                cursor: "help",
              }}
            >
              Container: {containerDisplay}
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            Container: {containerDisplay}
          </Typography>
        ))}
    </Stack>
  );
};

export function ShipmentReminderComponent({
  reminder: initialReminder,
  showShipmentOverviewInfo = false,
  onShipmentClick,
  onEmailOpen,
  onReminderUpdated,
}: ShipmentReminderComponentProps) {
  const [resolving, setResolving] = useState(false);
  const [reminder, setReminder] =
    useState<ReminderWithOptionalShipmentOverview>(initialReminder);

  const handleUpdateReminder = async (resolve: boolean) => {
    try {
      setResolving(true);

      await apiClient.post("/shipments/reminders/update-reminder", {
        reminderId: reminder.id,
        resolve,
        reason: resolve
          ? "Resolved from shipment timeline"
          : "Unresolved from shipment timeline",
      });

      // Update the local reminder state
      setReminder((prev) => ({
        ...prev,
        status: resolve ? "resolved" : "pending",
        resolvedTime: resolve ? new Date() : null,
      }));

      // Notify parent if callback provided
      if (onReminderUpdated) {
        onReminderUpdated(reminder.id, resolve);
      }
    } catch (err) {
      console.error("Error updating reminder:", err);
      throw err; // Let parent handle error display if needed
    } finally {
      setResolving(false);
    }
  };

  // Don't render if resolved
  if (reminder.status === "resolved") {
    return null;
  }

  const category = getReminderCategory(reminder.type);
  const isMilestone = category === "milestone";

  return (
    <Box
      sx={{
        border: isMilestone ? "1px solid #4caf50" : "1px solid #ffa726",
        borderRadius: "8px",
        backgroundColor: isMilestone ? "transparent" : "#fff3e0",
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* Icon */}
      {isMilestone ? (
        <FlagIcon sx={{ fontSize: "1.5rem", color: "#4caf50" }} />
      ) : (
        <WarningIcon sx={{ fontSize: "1.5rem", color: "#f57c00" }} />
      )}

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "100%",
          backgroundColor: isMilestone ? "#e0e0e0" : "#ffa726",
          alignSelf: "stretch",
        }}
      />

      {/* Date with Label */}
      <Box sx={{ minWidth: "90px" }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.7rem",
            fontWeight: 600,
            display: "block",
            mb: 0,
          }}
        >
          Created on
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.775rem",
          }}
        >
          {reminder.triggerTime &&
            format(new Date(reminder.triggerTime), "MMM d, yyyy")}
        </Typography>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "100%",
          backgroundColor: isMilestone ? "#e0e0e0" : "#ffa726",
          alignSelf: "stretch",
        }}
      />

      {/* Name, Description, and RenderInfo */}
      <Box sx={{ width: "400px" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {reminder.name}
          </Typography>
          {reminder.isInternal && (
            <Chip
              label="Internal"
              size="small"
              color="default"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
        </Stack>
        {reminder.description && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              display: "block",
              mt: 0.25,
            }}
          >
            {reminder.description}
          </Typography>
        )}
        {/* RenderInfo */}
        {reminder.renderInfo && reminder.renderInfo.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {reminder.renderInfo.map((info, index) => {
              const formattedValue =
                info.value instanceof Date
                  ? formatDateMonthDay(info.value)
                  : typeof info.value === "string" &&
                      !isNaN(Date.parse(info.value))
                    ? formatDateMonthDay(new Date(info.value))
                    : info.value;

              return (
                <Box
                  key={index}
                  sx={{
                    fontSize: "0.75rem",
                    mt: 0.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    {info.label}:
                  </Typography>
                  {info.shipmentEmailId && info.emailDisplayType === "info" ? (
                    // Email display type "info": show value with tiny email icon on the right
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                        }}
                      >
                        {formattedValue}
                      </Typography>
                      <EmailIcon
                        onClick={() =>
                          onEmailOpen && onEmailOpen(info.shipmentEmailId!)
                        }
                        sx={{
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          color: "primary.main",
                          "&:hover": {
                            color: "primary.dark",
                          },
                        }}
                      />
                    </Box>
                  ) : info.shipmentEmailId ? (
                    // Default: show email icon on the left with underlined value
                    <Box
                      component="span"
                      onClick={() =>
                        onEmailOpen && onEmailOpen(info.shipmentEmailId!)
                      }
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "primary.main",
                        fontSize: "0.75rem",
                        "&:hover": {
                          color: "primary.dark",
                        },
                      }}
                    >
                      <EmailIcon sx={{ fontSize: "0.875rem" }} />
                      {formattedValue}
                    </Box>
                  ) : (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.75rem",
                      }}
                    >
                      {formattedValue}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ShipmentOverviewTile */}
      {showShipmentOverviewInfo &&
        reminder.shipmentOverview &&
        reminder.shipmentFeedId && (
          <>
            {/* Divider */}
            <Box
              sx={{
                width: "1px",
                height: "100%",
                backgroundColor: isMilestone ? "#e0e0e0" : "#ffa726",
                alignSelf: "stretch",
              }}
            />
            <Box sx={{ minWidth: "200px" }}>
              <ShipmentOverviewTile
                shipmentOverview={reminder.shipmentOverview}
                onOpenShipment={
                  onShipmentClick
                    ? () => onShipmentClick(reminder.shipmentFeedId!)
                    : undefined
                }
              />
            </Box>
          </>
        )}

      {/* Assignee */}
      {reminder.assignedUsers && reminder.assignedUsers.length > 0 && (
        <>
          {/* Divider */}
          <Box
            sx={{
              width: "1px",
              height: "100%",
              backgroundColor: isMilestone ? "#e0e0e0" : "#ffa726",
              alignSelf: "stretch",
            }}
          />
          <Box sx={{ minWidth: "120px" }}>
            <Chip
              label={
                reminder.assignedUsers.length === 1
                  ? reminder.assignedUsers[0].name ||
                    reminder.assignedUsers[0].email.split("@")[0]
                  : `${reminder.assignedUsers[0].name || reminder.assignedUsers[0].email.split("@")[0]} +${reminder.assignedUsers.length - 1}`
              }
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.75rem",
                height: "24px",
              }}
            />
          </Box>
        </>
      )}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Button */}
      {isMilestone ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleUpdateReminder(true)}
          disabled={resolving}
          startIcon={<VisibilityOffIcon sx={{ fontSize: "1rem" }} />}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            fontSize: "0.8125rem",
            py: 0.5,
            px: 1.5,
            borderColor: "#4caf50",
            color: "#4caf50",
            "&:hover": {
              borderColor: "#388e3c",
              backgroundColor: "rgba(76, 175, 80, 0.04)",
            },
          }}
        >
          Hide
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleUpdateReminder(true)}
          disabled={resolving}
          startIcon={
            <CheckCircleIcon sx={{ fontSize: "1rem", color: "success.main" }} />
          }
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            fontSize: "0.8125rem",
            py: 0.5,
            px: 1.5,
            borderColor: "#f57c00",
            color: "#f57c00",
            "&:hover": {
              borderColor: "#ef6c00",
              backgroundColor: "rgba(245, 124, 0, 0.04)",
            },
          }}
        >
          Resolve
        </Button>
      )}
    </Box>
  );
}
